import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import {
  OpenDialogWithViewContainer,
  DialogActionTypes,
  DialogClosed,
  CloseDialog,
  OpenDialog,
  OpenConfirmationDialog,
  OpenDialogWithActionResult,
} from './dialog.actions';
import { mergeMap, filter, map, tap, first, switchMap } from 'rxjs/operators';
import { MatDialog, MatDialogRef, MatDialogConfig } from '@angular/material';
import { NGXLogger } from 'ngx-logger';
import { merge, race, pipe } from 'rxjs';
import { DigiAIXCoreAppState } from '../app-state';
import { Store, select } from '@ngrx/store';
import { getViewComponent } from '../tables/table.selectors';

export const DEFAULT_DIALOG_CONFIG: MatDialogConfig<any> = {
  maxWidth: '90vw',
  maxHeight: '90vh',
};

@Injectable()
export class DialogEffects<D> {
  public dialogRef = new Map<string, MatDialogRef<any>>();

  @Effect({ resubscribeOnError: true })
  public openDialog$ = this.actions$.pipe(
    ofType<OpenDialog<D>>(DialogActionTypes.OpenDialog),
    mergeMap((action) => {
      const ref = this.dialog.open(action.payload.componentRef, {
        ...DEFAULT_DIALOG_CONFIG,
        ...action.payload.config,
      });
      if (action.tracer) {
        if (this.dialogRef.has(action.tracer)) {
          this.logger.warn(
            `A dialog ref with tracer >${action.tracer}< is already open`,
          );
        }
        this.dialogRef.set(action.tracer, ref);
      }
      const closeIf = action.payload.closeIf || { actionType: null };

      const afterClosedPipe$ = pipe(
        filter(() => !!action.tracer),
        tap(() => this.dialogRef.delete(action.tracer)),
        map((result) => new DialogClosed(result || null, action.tracer)),
      );

      return race(
        ref.afterClosed().pipe(afterClosedPipe$),
        this.actions$.pipe(
          filter(() => !!closeIf.actionType),
          ofType(closeIf.actionType),
          filter((a) => (closeIf.filter ? closeIf.filter(a) : true)),
          first(),
          tap(() => ref.close(null)),
          afterClosedPipe$,
        ),
      );
    }),
  );

  @Effect({ resubscribeOnError: true })
  public openDialogWithActionResult$ = this.actions$.pipe(
    ofType<OpenDialogWithActionResult<D>>(
      DialogActionTypes.OpenDialogWithActionResult,
    ),
    mergeMap((action) => {
      const ref = this.dialog.open(action.payload.componentRef, {
        ...DEFAULT_DIALOG_CONFIG,
        ...action.payload.config,
      });
      if (action.tracer) {
        if (this.dialogRef.has(action.tracer)) {
          this.logger.warn(
            `A dialog ref with tracer >${action.tracer}< is already open`,
          );
        }
        this.dialogRef.set(action.tracer, ref);
      }
      const closeIf = action.payload.closeIf || { actionType: null };

      const afterClosedPipe$ = pipe(
        filter(() => !!action.tracer),
        tap(() => this.dialogRef.delete(action.tracer)),
        map((result) => new DialogClosed(result || null, action.tracer)),
      );

      return race(
        merge(
          ref.afterClosed().pipe(afterClosedPipe$),
          ref
            .afterClosed()
            .pipe(filter((result) => result && result.hasOwnProperty('type'))),
        ),
        this.actions$.pipe(
          filter(() => !!closeIf.actionType),
          ofType(closeIf.actionType),
          filter((a) => (closeIf.filter ? closeIf.filter(a) : true)),
          first(),
          tap(() => ref.close(null)),
          afterClosedPipe$,
        ),
      );
    }),
  );

  @Effect({ resubscribeOnError: true })
  public openDialogWithViewContainerAndResolveComponent$ = this.actions$.pipe(
    ofType<OpenDialogWithViewContainer<D>>(
      DialogActionTypes.OpenDialogWithViewContainer,
    ),
    filter((action) => !action.payload['componentRef']),
    switchMap((action) =>
      this.store$.pipe(
        select(getViewComponent(action.payload.config.data.tableId)),
        first(),
        filter((viewComponent) => !!viewComponent),
        map(
          (viewComponent) =>
            new OpenDialogWithViewContainer({
              componentRef: viewComponent,
              config: action.payload.config,
            }),
        ),
      ),
    ),
  );

  @Effect({ dispatch: false, resubscribeOnError: true })
  public closeDialog$ = this.actions$.pipe(
    ofType<CloseDialog>(DialogActionTypes.CloseDialog),
    filter((action) => this.dialogRef.has(action.tracer)),
    tap((action) => this.dialogRef.get(action.tracer).close(null)),
    tap((action) => this.dialogRef.delete(action.tracer)),
  );

  constructor(
    private actions$: Actions,
    protected store$: Store<DigiAIXCoreAppState>,
    private dialog: MatDialog,
    private logger: NGXLogger,
  ) {}
}
